import { ApolloError } from 'apollo-client';
import { GraphQLError } from 'graphql';

import { type AGGError } from '@atlassian/relay-errors';

export const isApolloError = (
	error: ApolloErrorInterop | ApolloError | GraphQLError | Error | undefined,
): error is ApolloErrorInterop => {
	return (
		typeof error === 'object' &&
		error != null &&
		(error.hasOwnProperty('graphQLErrors') || error.hasOwnProperty('networkError'))
	);
};

export class ApolloErrorInterop extends ApolloError {
	constructor(options: {
		graphQLErrors?: ReadonlyArray<GraphQLError | AGGError>;
		networkError?: Error | null;
		errorMessage?: string;
		extraInfo?: any;
	}) {
		// sometimes graphQLErrors isn't properly passed as an array (passed as typeof any)
		// we check if it's an array and if not, we wrap it in an array.
		// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
		const graphQLErrors = (
			Array.isArray(options.graphQLErrors) ? options.graphQLErrors : [options.graphQLErrors]
		)
			.filter(Boolean)
			.map((error) => {
				if (error instanceof GraphQLError) {
					return error;
				}

				const message = error.message;
				const extensions = error.extensions;
				const path = error.path;
				const originalError = new Error(message, {
					cause: error,
				});

				const graphQLError = new GraphQLError(
					message,
					undefined,
					undefined,
					undefined,
					path,
					originalError,
					extensions,
				);
				Object.defineProperties(graphQLError, {
					// Keep methods out of the enumerable server error payload, as GraphQLError does.
					toString: {
						// Relay errors have no source document; do not read the unsupported AST fields.
						value: () => message,
						writable: true,
						configurable: true,
						enumerable: false,
					},
					nodes: {
						get() {
							throw new Error(`[Relay migration] 'nodes' is not supported yet`);
						},
					},
					source: {
						get() {
							throw new Error(`[Relay migration] 'source' is not supported yet`);
						},
					},
					positions: {
						get() {
							throw new Error(`[Relay migration] 'positions' is not supported yet`);
						},
					},
				});
				return graphQLError;
			});

		super({
			...options,
			graphQLErrors,
		});

		// Fix prototype chain for instanceof checks
		// This is necessary when extending Error-based classes in JavaScript
		Object.setPrototypeOf(this, ApolloErrorInterop.prototype);
	}
}
